import {
  Gauge,
  Layers,
  Ruler,
  Fuel,
  Hash,
  Calendar,
  Timer,
  Circle,
  Zap,
  ArrowUp,
  Battery,
  Cog,
  Droplet,
} from 'lucide-react';
import { formatCapacity, formatHours, formatNumber } from '../../utils';
import type { EquipmentLine } from '../../types';

interface EquipmentSpecsProps {
  equipment: EquipmentLine;
}

interface SpecItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function SpecItem({ icon, label, value }: SpecItemProps) {
  if (!value || value === 'N/A') return null;

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-8 h-8 bg-hul-navy/10 rounded flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="font-medium text-hul-navy">{value}</div>
      </div>
    </div>
  );
}

export function EquipmentSpecs({ equipment }: EquipmentSpecsProps) {
  const specs = [
    {
      icon: <Gauge className="w-4 h-4 text-hul-navy" />,
      label: 'Capacity',
      value: formatCapacity(equipment.capacity),
    },
    {
      icon: <Layers className="w-4 h-4 text-hul-navy" />,
      label: 'Mast Type',
      value: equipment.mastType,
    },
    {
      icon: <Ruler className="w-4 h-4 text-hul-navy" />,
      label: 'Fork Length',
      value: equipment.forkLength,
    },
    {
      icon: <Fuel className="w-4 h-4 text-hul-navy" />,
      label: 'Fuel Type',
      value: equipment.fuelType,
    },
    ...(equipment.serialNumber
      ? [
          {
            icon: <Hash className="w-4 h-4 text-hul-navy" />,
            label: 'Serial #',
            value: equipment.serialNumber,
          },
        ]
      : []),
    ...(equipment.year
      ? [
          {
            icon: <Calendar className="w-4 h-4 text-hul-navy" />,
            label: 'Year',
            value: String(equipment.year),
          },
        ]
      : []),
    ...(equipment.hours !== undefined
      ? [
          {
            icon: <Timer className="w-4 h-4 text-hul-navy" />,
            label: 'Hours',
            value: formatHours(equipment.hours),
          },
        ]
      : []),
    // Extended specs - only show if they have values
    ...(equipment.tireType
      ? [
          {
            icon: <Circle className="w-4 h-4 text-hul-navy" />,
            label: 'Tire Type',
            value: equipment.tireType,
          },
        ]
      : []),
    ...(equipment.power
      ? [
          {
            icon: <Zap className="w-4 h-4 text-hul-navy" />,
            label: 'Power',
            value: equipment.power,
          },
        ]
      : []),
    ...(equipment.workHeight
      ? [
          {
            icon: <ArrowUp className="w-4 h-4 text-hul-navy" />,
            label: 'Work Height',
            value: equipment.workHeight,
          },
        ]
      : []),
    ...(equipment.overallHeight
      ? [
          {
            icon: <ArrowUp className="w-4 h-4 text-hul-navy" />,
            label: 'Overall Height',
            value: equipment.overallHeight,
          },
        ]
      : []),
    ...(equipment.platformHeight
      ? [
          {
            icon: <ArrowUp className="w-4 h-4 text-hul-navy" />,
            label: 'Platform Height',
            value: equipment.platformHeight,
          },
        ]
      : []),
    ...(equipment.forks
      ? [
          {
            icon: <Ruler className="w-4 h-4 text-hul-navy" />,
            label: 'Forks',
            value: equipment.forks,
          },
        ]
      : []),
    ...(equipment.batteryType
      ? [
          {
            icon: <Battery className="w-4 h-4 text-hul-navy" />,
            label: 'Battery Type',
            value: equipment.batteryType,
          },
        ]
      : []),
    ...(equipment.dieselEngine
      ? [
          {
            icon: <Cog className="w-4 h-4 text-hul-navy" />,
            label: 'Engine',
            value: equipment.dieselEngine,
          },
        ]
      : []),
    ...(equipment.fuelTank
      ? [
          {
            icon: <Droplet className="w-4 h-4 text-hul-navy" />,
            label: 'Fuel Tank',
            value: equipment.fuelTank,
          },
        ]
      : []),
  ];

  return (
    <div className="grid grid-cols-2 gap-2">
      {specs.map((spec, index) => (
        <SpecItem key={index} {...spec} />
      ))}
    </div>
  );
}

// Compact version for smaller displays
export function EquipmentSpecsCompact({ equipment }: EquipmentSpecsProps) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
      <span className="text-gray-600">
        <span className="font-medium">{formatCapacity(equipment.capacity)}</span> capacity
      </span>
      <span className="text-gray-600">
        <span className="font-medium">{equipment.fuelType}</span>
      </span>
      <span className="text-gray-600">
        <span className="font-medium">{equipment.mastType}</span>
      </span>
      {equipment.hours !== undefined && (
        <span className="text-gray-600">
          <span className="font-medium">{formatNumber(equipment.hours)}</span> hrs
        </span>
      )}
    </div>
  );
}
